import { t } from "@/i18n-js/instance";
import { useIsGlobalThemingEnabled } from "@/react/components/SettingsApp/Site/Pages/Edit/Design/useIsGlobalThemingEnabled";
import { useBuilderStore } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import type { PropsMeta } from "@circle-react-shared/BuilderUI/PropsMeta.types";
import type { StyleMetaAttributes } from "@circle-react-shared/BuilderUI/PropsMetaStyle.types";
import { useCustomColors } from "@circle-react-shared/BuilderUI/ThemeConfiguration/hooks/useCustomColors";
import { Form } from "@circle-react-uikit/Form";
import { AlignmentField } from "./fields/AlignmentField";
import { BorderRadiusSelector } from "./fields/BorderRadius";
import { BoxModelInput } from "./fields/BoxModelField";
import { FormCustomHtmlEditor } from "./fields/CustomHTMLEditor/FormCustomHtmlEditor";
import { DimensionInput } from "./fields/DimensionInput";
import { EmbedField } from "./fields/EmbedField";
import { HeadingLevelPicker } from "./fields/HeadingLevelPicker";
import { ImagePicker } from "./fields/ImagePicker";
import { ListField } from "./fields/ListField";
import { Sizing } from "./fields/Sizing";
import { SliderField } from "./fields/SliderField";
import { UrlField } from "./fields/UrlField";
import { VideoUploader } from "./fields/VideoUploader";

export interface EditorFieldProps {
  name: string;
  propMeta: PropsMeta | StyleMetaAttributes;
}

export const DefaultEditorFields = ({ name, propMeta }: EditorFieldProps) => {
  const customEditorFields = useBuilderStore(state => state.customEditorFields);
  const {
    flattenedCssVariables,
    themeColors,
    customColors,
    handleAddCustomColor,
  } = useCustomColors();
  const isGlobalThemingEnabled = useIsGlobalThemingEnabled();

  const fallbackLabel = propMeta.name;
  const i18nKey = propMeta.nameI18n;
  const label = i18nKey
    ? t(i18nKey, { defaultValue: fallbackLabel })
    : fallbackLabel;

  if (
    customEditorFields &&
    Object.prototype.hasOwnProperty.call(customEditorFields, propMeta.type)
  ) {
    const CustomFieldComponent = customEditorFields[propMeta.type];
    return <CustomFieldComponent name={name} propMeta={propMeta} />;
  }

  // Custom input fields should be handled by custom editor components.
  // This check here improves the type narrowing of the switch statement below.
  if (propMeta.prop === "custom") {
    return null;
  }

  switch (propMeta.type) {
    case "string":
      return (
        <Form.Item
          name={name}
          label={label}
          hideDescription
          hideBorder
          className="!p-0"
        >
          <Form.Input name={name} />
        </Form.Item>
      );

    case "textarea":
      return (
        <Form.Item
          name={name}
          label={label}
          hideDescription
          hideBorder
          className="!p-0"
        >
          <Form.Input name={name} variant="textarea" rows={7} />
        </Form.Item>
      );

    case "number":
      return (
        <Form.Item
          name={name}
          label={label}
          hideDescription
          hideBorder
          className="!p-0"
        >
          <Form.Input type="number" name={name} {...propMeta.numberProps} />
        </Form.Item>
      );

    case "boolean":
      return (
        <Form.Item
          name={name}
          label={label}
          hideDescription
          hideBorder
          className="!flex items-center !p-0"
          shouldToggleValueOnLabelClick
        >
          <Form.ToggleSwitch name={name} variant="small" />
        </Form.Item>
      );

    case "circle-image-upload":
    case "background-image":
      return (
        <Form.Item
          name={name}
          label={label}
          hideDescription
          hideBorder
          className="!p-0"
        >
          <ImagePicker
            name={name}
            shouldAllowRemovingImage={!!propMeta.shouldAllowRemovingImage}
          />
        </Form.Item>
      );

    case "circle-video-upload":
      return <VideoUploader name={name} />;

    case "color":
      return (
        <Form.Item
          name={name}
          label={label}
          hideDescription
          hideBorder
          labelClassName="!m-0"
          className="!flex !w-full !items-center !justify-between !p-0"
        >
          <Form.ColorPickerV2
            name={name}
            cssVariables={flattenedCssVariables}
            shouldResolveCssVariable={Boolean(flattenedCssVariables)}
            shouldRenderThemeColors={isGlobalThemingEnabled}
            themeColors={themeColors}
            customColors={customColors}
            onAddCustomColor={
              isGlobalThemingEnabled ? handleAddCustomColor : undefined
            }
          />
        </Form.Item>
      );

    case "select": {
      return (
        <Form.Item
          name={name}
          label={label}
          hideDescription
          hideBorder
          className="!p-0"
        >
          <Form.SelectNative
            name={name}
            options={propMeta.options}
            placeholder={label}
          />
        </Form.Item>
      );
    }

    case "custom-html":
      return (
        <Form.Item
          name={name}
          label={label}
          description={t("builder_ui.custom_html.disclaimer_message")}
          hideBorder
          className="!p-0"
        >
          <div className="mt-2" />
          <FormCustomHtmlEditor name={name} />
        </Form.Item>
      );

    case "list":
      return <ListField name={name} />;

    case "oembed":
      return <EmbedField name={name} label={label} />;

    case "slider": {
      return (
        <SliderField
          name={name}
          label={label}
          inputClassName="w-16"
          {...propMeta.sliderProps}
        />
      );
    }

    case "url":
      return <UrlField name={name} label={label} />;

    case "border-radius":
      return <BorderRadiusSelector name={name} />;

    case "alignment":
      return (
        <AlignmentField
          name={name}
          options={propMeta.alignment_options}
          label={label}
        />
      );

    case "dimension":
      return <DimensionInput name={name} label={label} />;

    case "sizing":
      return <Sizing />;

    case "box-model":
      return (
        <BoxModelInput
          name={name}
          label={label}
          type={propMeta.boxModelType ?? "padding"}
        />
      );

    case "heading-level-picker":
      return <HeadingLevelPicker label={label} />;

    default:
      return null;
  }
};
